import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ChatRoom, ChatParticipant, ResolutionCenterService } from '@app/resolution-center/resolution-center.service';

@Component({
    selector: 'app-chat-room-details-sidebar',
    templateUrl: './chat-room-details-sidebar.component.html',
    styleUrls: ['./chat-room-details-sidebar.component.scss'],
    standalone: false
})
export class ChatRoomDetailsSidebarComponent implements OnChanges {
    @Input() chatRoom?: ChatRoom | null;
    @Input() isCoach: boolean = false;
    @Output() editCustomChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to edit a custom chat room
    @Output() editSystemChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to edit a system chat room
    @Output() addMembersToChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to add members via chat room modal

    memberSearchQuery: string = '';
    displayedMembers: ChatParticipant[] = [];
    readonly MAX_DISPLAYED_MEMBERS = 5;
    membersSearchLoading = false;
    private memberSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    showManageMembersModal = false;
    openMemberMenuId: number | null = null;
    submenuPosition: { top: number; left: number } | null = null;

    constructor(
        private router: Router,
        private resolutionCenterService: ResolutionCenterService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['chatRoom']) {
            this.memberSearchQuery = '';
            this.updateDisplayedMembers();
        }
    }

    onEditCustomGroupChat(): void {
        if (!this.chatRoom) return;

        // Emit event to parent to open edit modal for custom chat room
        this.editCustomChatRoom.emit(this.chatRoom);
    }

    onEditSystemGroupChat(): void {
        if (!this.chatRoom) return;

        // Emit event to parent to open edit modal for system chat room
        this.editSystemChatRoom.emit(this.chatRoom);
    }

    onMemberSearchChange(): void {
        if (this.memberSearchDebounceTimer) {
            clearTimeout(this.memberSearchDebounceTimer);
        }
        this.memberSearchDebounceTimer = setTimeout(() => {
            this.loadMembersForSearch();
            this.memberSearchDebounceTimer = null;
        }, 300);
    }

    private async loadMembersForSearch(): Promise<void> {
        if (!this.chatRoom?.id) {
            this.displayedMembers = [];
            return;
        }

        const query = this.memberSearchQuery?.trim() || '';

        if (!query) {
            this.updateDisplayedMembersFromRoom();
            return;
        }

        this.membersSearchLoading = true;
        const { data, error } = await this.resolutionCenterService.getChatRoomMembers(
            this.chatRoom.id,
            query,
            1
        );
        this.membersSearchLoading = false;

        if (error || !data) {
            this.displayedMembers = [];
            return;
        }

        this.displayedMembers = data.data || [];
    }

    private updateDisplayedMembersFromRoom(): void {
        if (!this.chatRoom?.members) {
            this.displayedMembers = [];
            return;
        }
        this.displayedMembers = this.chatRoom.members.slice(0, this.MAX_DISPLAYED_MEMBERS);
    }

    private updateDisplayedMembers(): void {
        this.updateDisplayedMembersFromRoom();
    }

    getInitials(name?: string): string {
        if (!name) {
            if (!this.chatRoom) return '?';
            name = this.chatRoom.custom_name || this.chatRoom.name;
        }
        
        if (!name) return '?';
        
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    getMemberInitials(member: ChatParticipant): string {
        return this.getInitials(member.name);
    }

    onSeeAllMembers($event: Event): void {
        $event.preventDefault();
        if (!this.chatRoom) return;
        this.showManageMembersModal = true;
    }

    onAddNewMembers($event: Event): void {
        $event.preventDefault();
        if (!this.chatRoom) return;
        this.addMembersToChatRoom.emit(this.chatRoom);
    }

    onAddNewMembersFromManageModal(): void {
        this.showManageMembersModal = false;
        if (!this.chatRoom) return;
        this.addMembersToChatRoom.emit(this.chatRoom);
    }

    onCloseManageMembersModal(): void {
        this.showManageMembersModal = false;
    }

    @HostListener('document:click')
    closeMemberMenu(): void {
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }

    onToggleMemberMenu(event: Event, member: ChatParticipant): void {
        event.stopPropagation();
        if (this.openMemberMenuId === member.id) {
            this.openMemberMenuId = null;
            this.submenuPosition = null;
            return;
        }
        const el = (event.target as HTMLElement).closest('.action') as HTMLElement;
        if (el) {
            const rect = el.getBoundingClientRect();
            const submenuWidth = 180;
            this.submenuPosition = {
                top: rect.bottom + 4,
                left: Math.max(8, rect.right - submenuWidth - 4)
            };
        }
        this.openMemberMenuId = member.id;
    }

    getOpenMember(): ChatParticipant | null {
        if (this.openMemberMenuId == null) return null;
        return this.displayedMembers.find(m => m.id === this.openMemberMenuId) ?? null;
    }

    onOpenMemberProfile(member: ChatParticipant): void {
        if (!member?.id) return;
        window.open(`/user/user/details-mantis/${member.id}`, '_blank');
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }

    onMessageMember(member: ChatParticipant): void {
        if (!member?.id) return;
        this.resolutionCenterService.openDirectChatRequest = { userId: member.id };
        this.router.navigate(['/client-messages']);
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }
}

